import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Toggle } from "@/components/ui/toggle";
import type { FeedbackCategory, FeedbackContentType } from "@/types/feedback";

const SHARED_CATEGORIES: readonly FeedbackCategory[] = [
  "inaccurate_information",
  "invented_detail",
  "missing_information",
  "wrong_wording",
  "irrelevant_information",
  "other",
];

const SYNOPSIS_CATEGORIES: readonly FeedbackCategory[] = [
  "factually_wrong",
  "invented_information",
  "key_events_missing",
  "irrelevant_information",
  "structure_hard_to_follow",
  "other",
];

const SUMMARY_CATEGORIES: readonly FeedbackCategory[] = [
  "factually_wrong",
  "invented_information",
  "key_events_missing",
  "irrelevant_information",
  "structure_hard_to_follow",
  "weak_character_description",
  "character_context_missing",
  "other",
];

const CATEGORIES_BY_TYPE: Record<
  FeedbackContentType,
  readonly FeedbackCategory[]
> = {
  synopsis: SYNOPSIS_CATEGORIES,
  summary: SUMMARY_CATEGORIES,
  book_details: SHARED_CATEGORIES,
  characters: SHARED_CATEGORIES,
  world_setting: SHARED_CATEGORIES,
  tropes: SHARED_CATEGORIES,
};

const SHARED_LABEL_KEY: Partial<Record<FeedbackCategory, string>> = {
  inaccurate_information: "feedback.categories.shared.inaccurateInformation",
  invented_detail: "feedback.categories.shared.inventedDetail",
  missing_information: "feedback.categories.shared.missingInformation",
  wrong_wording: "feedback.categories.shared.wrongWording",
  irrelevant_information: "feedback.categories.shared.irrelevantInformation",
  other: "feedback.categories.shared.other",
};

const SYNOPSIS_LABEL_KEY: Partial<Record<FeedbackCategory, string>> = {
  factually_wrong: "feedback.categories.synopsis.factuallyWrong",
  invented_information: "feedback.categories.synopsis.inventedInformation",
  key_events_missing: "feedback.categories.synopsis.keyEventsMissing",
  irrelevant_information: "feedback.categories.synopsis.irrelevantInformation",
  structure_hard_to_follow:
    "feedback.categories.synopsis.structureHardToFollow",
  other: "feedback.categories.synopsis.other",
};

const SUMMARY_LABEL_KEY: Partial<Record<FeedbackCategory, string>> = {
  factually_wrong: "feedback.categories.summary.factuallyWrong",
  invented_information: "feedback.categories.summary.inventedInformation",
  key_events_missing: "feedback.categories.summary.keyEventsMissing",
  irrelevant_information: "feedback.categories.summary.irrelevantInformation",
  structure_hard_to_follow: "feedback.categories.summary.structureHardToFollow",
  weak_character_description:
    "feedback.categories.summary.weakCharacterDescription",
  character_context_missing:
    "feedback.categories.summary.characterContextMissing",
  other: "feedback.categories.summary.other",
};

function categoryLabelKey(
  contentType: FeedbackContentType,
  category: FeedbackCategory,
): string {
  if (contentType === "synopsis") {
    return SYNOPSIS_LABEL_KEY[category] ?? category;
  }
  if (contentType === "summary") {
    return SUMMARY_LABEL_KEY[category] ?? category;
  }
  return SHARED_LABEL_KEY[category] ?? category;
}

interface FeedbackCategoryChipsProps {
  contentType: FeedbackContentType;
  selected: FeedbackCategory[];
  onToggle: (category: FeedbackCategory) => void;
  hideLegend?: boolean;
}

export function FeedbackCategoryChips({
  contentType,
  selected,
  onToggle,
  hideLegend = false,
}: FeedbackCategoryChipsProps) {
  const { t } = useTranslation("jobDetails");
  const categories = CATEGORIES_BY_TYPE[contentType];

  return (
    <fieldset>
      {!hideLegend && (
        <legend className="text-sm font-medium text-foreground mb-2">
          {t("feedback.whatWentWrong")}
        </legend>
      )}
      <div className="flex flex-wrap gap-2 max-w-[80%]" role="group">
        {categories.map((category) => {
          const isSelected = selected.includes(category);
          const label = t(categoryLabelKey(contentType, category));
          return (
            <Toggle
              key={category}
              pressed={isSelected}
              onPressedChange={() => onToggle(category)}
              variant="outline"
              size="sm"
              className={cn(
                "rounded-full h-7 px-3 text-xs font-medium gap-1.5",
                isSelected
                  ? "!bg-white !border-primary-500 !text-primary-500 data-[state=on]:!bg-white data-[state=on]:text-primary-500"
                  : "!bg-white border-slate-200 text-secondary-600 hover:border-slate-300 hover:!bg-white",
              )}
              aria-label={label}
              data-testid={`feedback-category-${category}`}
            >
              {label}
            </Toggle>
          );
        })}
      </div>
    </fieldset>
  );
}
